// Livraison AMEEX — « Watch, don't click » (owner, 2026-10-10).
//
// « Today I never open it: it's a long list of buttons and the statuses look
// wrong. » Rebuilt as four numbers, four lanes of parcels that move by
// themselves when AMEEX sends an update, one panel per parcel, and two small
// cards: « À expédier » (the truly ready orders, one button) and « À vérifier »
// (what nobody will move without a decision).
//
// Real data only: lanes from our mirror of AMEEX's status (webhook + the
// 6-hour sync), COD and fees from AMEEX's parcel list, the panel live from
// AMEEX. GET /ameex/board, GET /ameex/parcel/:code (worker routes/ameex.ts).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ameexApi, type AmeexBoardParcel } from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { useAmeexConfirmPrint, AmeexConfirmPrintButton, AmeexBatchResult } from '@/components/admin/AmeexConfirmPrint';
import { useT } from '@/lib/i18n';
import { useIsPhone } from '@/lib/phoneViewport';
import { DeliveryBoard } from './delivery/DeliveryBoard';
import { ParcelPanel } from './delivery/ParcelPanel';
import { CHECK_LABEL } from './delivery/board';

const BOARD_KEY = ['ameex-dashboard', 'board'] as const;

export default function AdminDeliveryAmeexPage() {
  const { t, lang } = useT();
  const phone = useIsPhone();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: BOARD_KEY,
    queryFn: () => ameexApi.board(),
    // A chip moves when AMEEX's webhook lands; the board asks every 15 s.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(id); }, []);
  const [selected, setSelected] = useState<string | null>(null);
  const [showOwed, setShowOwed] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const cp = useAmeexConfirmPrint(() => { void refetch(); });

  const money = (n: number) => `${Math.round(n).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR')} Dh`;
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit' }) : '—');

  const parcel: AmeexBoardParcel | null = data?.parcels.find((p) => p.code === selected) ?? null;
  const n = data?.numbers;

  const card = 'rounded-2xl border px-5 py-4 text-left';
  return (
    <AdminLayout title={t('Livraison AMEEX')} subtitle={t('Les colis bougent tout seuls quand AMEEX envoie une mise à jour.')}>
      <div className="max-w-6xl mx-auto space-y-5">
        {isLoading && !data ? (
          <div className="rounded-2xl border border-border-subtle bg-bg-card p-10 text-center text-[13px] text-text-tertiary">{t('Chargement…')}</div>
        ) : isError || !data || !n ? (
          <div className="rounded-2xl border border-status-waiting-border bg-status-waiting-bg p-6 text-[13px] text-status-waiting">
            {t('Impossible de charger les livraisons. La page réessaie toute seule.')}
          </div>
        ) : (
          <>
            {/* ─── 1. FOUR NUMBERS ─────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className={`${card} border-border-subtle bg-bg-card`}>
                <p className="text-[13px] text-text-secondary">{t('En route')}</p>
                <p className="font-heading text-[30px] leading-tight tabular-nums text-text-primary">{n.enRoute}</p>
              </div>
              <div className={`${card} border-border-subtle bg-bg-card`}>
                <p className="text-[13px] text-text-secondary">{t('Livrés · 7 j')}</p>
                <p className="font-heading text-[30px] leading-tight tabular-nums text-text-primary">{n.delivered7}</p>
              </div>
              <div className={`${card} ${n.problems ? 'border-status-stuck-border bg-status-stuck-bg text-status-stuck' : 'border-border-subtle bg-bg-card text-text-primary'}`}>
                <p className="text-[13px] opacity-90">{t('Problème')}</p>
                <p className="font-heading text-[30px] leading-tight tabular-nums">{n.problems}</p>
              </div>
              <button type="button" onClick={() => setShowOwed((v) => !v)} aria-expanded={showOwed}
                className={`${card} cursor-pointer border-status-done-border bg-status-done-bg text-status-done focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}>
                <p className="text-[13px] opacity-90">{t('AMEEX te doit')}</p>
                <p className="font-heading text-[30px] leading-tight tabular-nums">{n.owed ? money(n.owed.amount) : '—'}</p>
              </button>
            </div>
            {showOwed && (
              <div className="rounded-2xl border border-border-subtle bg-bg-card px-5 py-4 text-[13.5px] text-text-secondary">
                {n.owed ? (
                  <>
                    <p className="text-text-primary">
                      {t('COD livrés depuis le {d} : {cod} − frais AMEEX : {fees} − versements reçus : {paid} = {owed}', {
                        d: day(n.owed.since), cod: money(n.owed.cod), fees: money(n.owed.fees), paid: money(n.owed.payouts), owed: money(n.owed.amount),
                      })}
                    </p>
                    <p className="mt-1 text-[12.5px] text-text-tertiary">
                      {t('{n} colis livrés. Dernier versement saisi : {d}. AMEEX ne dit pas quel colis il a payé — les versements viennent de Comptabilité.', { n: n.owed.parcels, d: day(n.owed.lastPayoutAt) })}
                    </p>
                  </>
                ) : <p>{t('La liste des colis AMEEX n’a pas répondu — le montant reviendra au prochain chargement.')}</p>}
              </div>
            )}

            {/* ─── 2. À EXPÉDIER — a small card, never the page ───────────── */}
            <div className="rounded-2xl border border-border-subtle bg-bg-card px-5 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-heading text-[18px] text-text-primary flex-1">
                  {t('À expédier')} <span className="tabular-nums">· {data.ready.length}</span>
                </h2>
                {data.ready.length > 0 && (
                  <AmeexConfirmPrintButton ids={data.ready.map((o) => o.shopify_order_id)} run={cp.run} isPending={cp.isPending}
                    label={t('Créer les colis')}
                    className="inline-flex items-center justify-center rounded-xl px-4 min-h-[44px] text-[14px] font-semibold bg-brand-500 text-[#1C150E] dark:text-[#1C150E] hover:bg-brand-600 disabled:opacity-50 cursor-pointer" />
                )}
              </div>
              {data.ready.length ? (
                <p className="mt-1 text-[13px] text-text-secondary">
                  {data.ready.slice(0, 6).map((o) => `${o.customer_name ?? o.shopify_order_name} · ${o.delivery_city ?? '—'}`).join('  ·  ')}
                  {data.ready.length > 6 ? ` +${data.ready.length - 6}` : ''}
                </p>
              ) : (
                <p className="mt-1 text-[13px] text-text-tertiary">
                  {t('Rien de prêt : aperçu validé, prêt à imprimer et adresse complète. {n} commandes encore en préparation.', { n: data.notYet })}
                </p>
              )}
              <div className="mt-2"><AmeexBatchResult summary={cp.summary} lastBatch={cp.lastBatch} results={cp.results} orders={data.ready} onRevalidate={() => cp.run(cp.lastIds)} isPending={cp.isPending} /></div>
            </div>

            {/* ─── 3. THE LANES ────────────────────────────────────────────── */}
            {!data.listOk && <p className="text-[12.5px] text-status-waiting">{t('AMEEX n’a pas renvoyé sa liste : villes et COD peuvent manquer un instant.')}</p>}
            <DeliveryBoard parcels={data.parcels} now={now} selected={selected} isPhone={phone}
              onSelect={(p) => setSelected((cur) => (cur === p.code ? null : p.code))} />

            {/* ─── 4. ONE PANEL ────────────────────────────────────────────── */}
            {parcel && <ParcelPanel key={parcel.code} parcel={parcel} now={now} full={phone} onClose={() => setSelected(null)} />}

            {/* ─── 5. À VÉRIFIER ───────────────────────────────────────────── */}
            {data.toCheck.length > 0 && (
              <div className="rounded-2xl border border-border-subtle bg-bg-card">
                <button type="button" onClick={() => setShowCheck((v) => !v)} aria-expanded={showCheck}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 rounded-2xl">
                  <span className="font-heading text-[18px] text-text-primary flex-1">{t('À vérifier')} <span className="tabular-nums">· {data.toCheck.length}</span></span>
                  <span className="text-[13px] text-text-tertiary">{showCheck ? t('Masquer') : t('Voir')}</span>
                </button>
                {showCheck && (
                  <ul className="max-h-[320px] overflow-y-auto divide-y divide-border-subtle border-t border-border-subtle">
                    {data.toCheck.map((c) => (
                      <li key={`${c.kind}-${c.orderId}-${c.code ?? ''}`}>
                        <Link to={`/admin/orders/${c.shopifyOrderId}`}
                          className="flex items-center gap-3 px-5 py-3 min-h-[48px] hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500">
                          <span className="min-w-0 flex-1">
                            <span className="block text-[14px] text-text-primary truncate">{c.orderName} · {c.name ?? t('Sans nom')}</span>
                            <span className="block text-[12.5px] text-text-tertiary truncate">
                              {t(CHECK_LABEL[c.reason])}{c.missing?.length ? ` — ${c.missing.map((m) => t(m)).join(', ')}` : ''}
                            </span>
                          </span>
                          <span className="shrink-0 text-[12px] text-text-tertiary tabular-nums">{day(c.lastEventAt ?? c.createdAt ?? null)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
